import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { filterRolesByHierarchy } from '../../utils/roleUtils';
import UserForm from '../../components/forms/UserForm';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IRole {
  _id: string;
  name: string;
  permissions?: Record<string, number>;
  isSystem?: boolean;
}

const UserCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { currentMembership } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<IRole[]>([]);
  const navigate = useNavigate();

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await api.roles.list();
      const allRoles = data as IRole[];
      const filtered = filterRolesByHierarchy(allRoles, currentMembership?.role as IRole | undefined);
      setRoles(filtered as IRole[]);
    } catch {
      // Non-critical — form still works without role dropdown
    }
  }, [currentMembership]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const submitHandler = async (data: { name: string; email: string; phoneNumber: string; password?: string; roleId?: string }) => {
    setLoading(true);
    setError(null);

    try {
      await api.users.create({
        name: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        password: data.password!,
        roleId: data.roleId,
      });
      setLoading(false);
      addToast(t('users.createTitle') + ' - ' + t('common.save') + '!', 'success');
      navigate('/admin/users');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
    }
  };

  return (
    <div className="admin-form-page">
      <h2>{t('users.createTitle')}</h2>
      {loading && <p>{t('common.creating')}</p>}
      {error && <p className="error-message">{t('common.error')}: {error}</p>}
      <UserForm
        onSubmit={submitHandler}
        loading={loading}
        submitButtonText={t('users.createButton')}
        roles={roles}
      />
      <Link to="/admin/users" className="form-link">{t('users.goBack')}</Link>
    </div>
  );
};

export default UserCreatePage;
